import React, { useState, useMemo } from "react";
import Select from "react-select";
import "./Vestiario.css";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";

export default function Vestiario() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);
  const { documents: vestiario, error: vestiarioError } =
    useCollection("vestiario");
  const { documents: dipendenti } = useCollection("users");
  const { addDocument, updateDocument, deleteDocument, response } =
    useFirestore("vestiario");

  const [activeTab, setActiveTab] = useState("inventario");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    tipo: "",
    taglia: "",
    quantita: 0,
    descrizione: "",
    dataAcquisto: "",
  });

  const [assignData, setAssignData] = useState({
    dipendenteId: "",
    dipendenteName: "",
    quantitaAssegnata: 1,
    dataAssegnazione: new Date().toISOString().split("T")[0],
    note: "",
  });

  // Filtri per l'inventario
  const [filters, setFilters] = useState({
    tipo: null,
    taglia: null,
    disponibilita: null,
  });

  const tipiVestiario = [
    "T-Shirt",
    "Polo",
    "Felpa",
    "Giacca Leggera",
    "Giacca Invernale",
    "Smanicato",
    "Scarpe antinfortunistiche",
    "Casco",
    "Guanti",
    "Gilet alta visibilità",
    "Tuta da lavoro",
    "Zaino",
    "Borsello",
    "Altro",
  ];

  const taglie = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "n/a"];

  // Opzioni per react-select
  const tipoOptions = tipiVestiario.map((tipo) => ({
    value: tipo,
    label: tipo,
  }));
  const tagliaOptions = taglie.map((taglia) => ({
    value: taglia,
    label: taglia,
  }));
  const disponibilitaOptions = [
    { value: "disponibile", label: "Disponibile" },
    { value: "esaurito", label: "Esaurito" },
    { value: "parziale", label: "Parzialmente disponibile" },
  ];

  // Filtra gli articoli in base ai filtri selezionati
  const filteredVestiario = useMemo(() => {
    if (!vestiario) return [];

    return vestiario.filter((item) => {
      // Filtro per tipo
      if (filters.tipo && item.tipo !== filters.tipo.value) return false;

      // Filtro per taglia
      if (filters.taglia && item.taglia !== filters.taglia.value) return false;

      // Filtro per disponibilità
      if (filters.disponibilita) {
        const disponibile = item.quantitaDisponibile > 0;
        const esaurito = item.quantitaDisponibile === 0;
        const parziale =
          item.quantitaDisponibile > 0 &&
          item.quantitaDisponibile < item.quantita;

        switch (filters.disponibilita.value) {
          case "disponibile":
            if (!disponibile || parziale) return false;
            break;
          case "esaurito":
            if (!esaurito) return false;
            break;
          case "parziale":
            if (!parziale) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  }, [vestiario, filters]);

  // Reset filtri
  const resetFilters = () => {
    setFilters({
      tipo: null,
      taglia: null,
      disponibilita: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newItem = {
      ...formData,
      quantita: parseInt(formData.quantita),
      quantitaDisponibile: parseInt(formData.quantita),
      assegnazioni: [],
      createdAt: new Date(),
      createdBy: user.uid,
    };

    await addDocument(newItem);

    if (!response.error) {
      setFormData({
        tipo: "",
        taglia: "",
        quantita: 0,
        descrizione: "",
        dataAcquisto: "",
      });
      setShowAddForm(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();

    if (
      !selectedItem ||
      assignData.quantitaAssegnata > selectedItem.quantitaDisponibile
    ) {
      alert("Quantità non disponibile");
      return;
    }

    const nuovaAssegnazione = {
      dipendenteId: assignData.dipendenteId,
      dipendenteName: assignData.dipendenteName,
      quantita: parseInt(assignData.quantitaAssegnata),
      dataAssegnazione: assignData.dataAssegnazione,
      note: assignData.note,
      assegnatoDa: user.uid,
    };

    const updatedItem = {
      ...selectedItem,
      quantitaDisponibile:
        selectedItem.quantitaDisponibile -
        parseInt(assignData.quantitaAssegnata),
      assegnazioni: [...selectedItem.assegnazioni, nuovaAssegnazione],
    };

    await updateDocument(selectedItem.id, updatedItem);

    if (!response.error) {
      setAssignData({
        dipendenteId: "",
        dipendenteName: "",
        quantitaAssegnata: 1,
        dataAssegnazione: new Date().toISOString().split("T")[0],
        note: "",
      });
      setShowAssignForm(false);
      setSelectedItem(null);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Sei sicuro di voler eliminare questo articolo?")) {
      await deleteDocument(itemId);
    }
  };

  const handleAnnullaAssegnazione = async (
    itemId,
    dipendenteId,
    dataAssegnazione
  ) => {
    if (window.confirm("Sei sicuro di voler annullare questa assegnazione?")) {
      const item = vestiario.find((v) => v.id === itemId);
      if (!item) return;

      // Trova l'assegnazione specifica da rimuovere
      const assegnazioneIndex = item.assegnazioni.findIndex(
        (a) =>
          a.dipendenteId === dipendenteId &&
          a.dataAssegnazione === dataAssegnazione
      );

      if (assegnazioneIndex === -1) return;

      const assegnazione = item.assegnazioni[assegnazioneIndex];
      const nuoveAssegnazioni = item.assegnazioni.filter(
        (_, index) => index !== assegnazioneIndex
      );

      const updatedItem = {
        ...item,
        quantitaDisponibile: item.quantitaDisponibile + assegnazione.quantita,
        assegnazioni: nuoveAssegnazioni,
      };

      await updateDocument(itemId, updatedItem);
    }
  };

  const getAssegnazioniDipendente = (dipendenteId) => {
    if (!vestiario) return [];

    const assegnazioni = [];
    vestiario.forEach((item) => {
      item.assegnazioni?.forEach((assegnazione) => {
        if (assegnazione.dipendenteId === dipendenteId) {
          assegnazioni.push({
            ...assegnazione,
            itemTipo: item.tipo,
            itemTaglia: item.taglia,
            itemId: item.id,
          });
        }
      });
    });

    return assegnazioni;
  };

  if (!admin) {
    return (
      <div className="vestiario-container">
        <div className="access-denied">
          <h2>Accesso Negato</h2>
          <p>Non hai i permessi per accedere a questa sezione.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vestiario-container">
      <div className="vestiario-header">
        <h1>Gestione Vestiario Dipendenti</h1>
        <div className="tab-buttons">
          <button
            className={`tab-btn ${activeTab === "inventario" ? "active" : ""}`}
            onClick={() => setActiveTab("inventario")}
          >
            Inventario
          </button>
          <button
            className={`tab-btn ${
              activeTab === "assegnazioni" ? "active" : ""
            }`}
            onClick={() => setActiveTab("assegnazioni")}
          >
            Assegnazioni
          </button>
        </div>
      </div>

      {activeTab === "inventario" && (
        <div className="inventario-section">
          <div className="section-header">
            <h2>Inventario Vestiario</h2>
            <button
              className="btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              Aggiungi Articolo
            </button>
          </div>

          {vestiarioError && (
            <div className="error-message">
              Errore nel caricamento dei dati: {vestiarioError}
            </div>
          )}

          {/* Sezione Filtri */}
          <div className="filters-section">
            <h4>Filtri</h4>
            <div className="filters-grid">
              <div className="filter-group">
                <label>Tipo</label>
                <Select
                  value={filters.tipo}
                  onChange={(selectedOption) =>
                    setFilters((prev) => ({ ...prev, tipo: selectedOption }))
                  }
                  options={tipoOptions}
                  placeholder="Seleziona tipo..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 50 }),
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Taglia</label>
                <Select
                  value={filters.taglia}
                  onChange={(selectedOption) =>
                    setFilters((prev) => ({ ...prev, taglia: selectedOption }))
                  }
                  options={tagliaOptions}
                  placeholder="Seleziona taglia..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 50 }),
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Disponibilità</label>
                <Select
                  value={filters.disponibilita}
                  onChange={(selectedOption) =>
                    setFilters((prev) => ({
                      ...prev,
                      disponibilita: selectedOption,
                    }))
                  }
                  options={disponibilitaOptions}
                  placeholder="Seleziona disponibilità..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 50 }),
                  }}
                />
              </div>

              <div className="filter-group">
                <button className="btn-reset-filters" onClick={resetFilters}>
                  Reset Filtri
                </button>
              </div>
            </div>
          </div>

          {filteredVestiario && filteredVestiario.length > 0 ? (
            <div className="inventario-grid">
              {filteredVestiario.map((item) => (
                <div key={item.id} className="inventario-card">
                  <div className="card-header">
                    <h3>{item.tipo}</h3>
                    <div className="card-actions">
                      <button
                        className="btn-assign"
                        onClick={() => {
                          setSelectedItem(item);
                          setShowAssignForm(true);
                        }}
                        disabled={item.quantitaDisponibile === 0}
                      >
                        Assegna
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        X
                      </button>
                    </div>
                  </div>
                  <div className="card-details">
                    <p>
                      <strong>Taglia:</strong> {item.taglia}
                    </p>
                    <p>
                      <strong>Quantità totale:</strong> {item.quantita}
                    </p>
                    <p
                      className={`disponibilita ${
                        item.quantitaDisponibile === 0
                          ? "esaurito"
                          : item.quantitaDisponibile < item.quantita
                          ? "parziale"
                          : "disponibile"
                      }`}
                    >
                      <strong>Disponibile:</strong> {item.quantitaDisponibile}
                    </p>
                    <p>
                      <strong>Assegnate:</strong>{" "}
                      {item.quantita - item.quantitaDisponibile}
                    </p>
                    {item.descrizione && (
                      <p>
                        <strong>Descrizione:</strong> {item.descrizione}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              {vestiario && vestiario.length > 0 ? (
                <p>Nessun articolo corrisponde ai filtri selezionati.</p>
              ) : (
                <p>Nessun articolo presente nell'inventario.</p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "assegnazioni" && (
        <div className="assegnazioni-section">
          <h2>Assegnazioni per Dipendente</h2>
          <div className="dipendenti-grid">
            {dipendenti &&
              dipendenti.map((dipendente) => {
                const assegnazioni = getAssegnazioniDipendente(dipendente.id);
                return (
                  <div key={dipendente.id} className="dipendente-card">
                    <h3>
                      {dipendente.anagrafica?.nome &&
                      dipendente.anagrafica?.cognome
                        ? `${dipendente.anagrafica.nome} ${dipendente.anagrafica.cognome}`
                        : dipendente.displayName}
                    </h3>
                    <div className="assegnazioni-list">
                      {assegnazioni.length > 0 ? (
                        assegnazioni.map((assegnazione, index) => (
                          <div key={index} className="assegnazione-item">
                            <div className="assegnazione-content">
                              <p>
                                <strong>{assegnazione.itemTipo}</strong> -{" "}
                                {assegnazione.itemTaglia}
                              </p>
                              <p>Quantità: {assegnazione.quantita}</p>
                              <p>
                                Data:{" "}
                                {new Date(
                                  assegnazione.dataAssegnazione
                                ).toLocaleDateString()}
                              </p>
                              {assegnazione.note && (
                                <p>Note: {assegnazione.note}</p>
                              )}
                            </div>
                            <button
                              className="btn-annulla-assegnazione"
                              onClick={() =>
                                handleAnnullaAssegnazione(
                                  assegnazione.itemId,
                                  assegnazione.dipendenteId,
                                  assegnazione.dataAssegnazione
                                )
                              }
                              title="Annulla assegnazione"
                            >
                              ×
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="no-assegnazioni">Nessuna assegnazione</p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Modal Aggiungi Articolo */}
      {showAddForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <div className="modal-header">
              <h3>Aggiungi Nuovo Articolo</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="add-form">
              <div className="form-group">
                <label>Tipo:</label>
                <Select
                  value={
                    tipoOptions.find(
                      (option) => option.value === formData.tipo
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    setFormData({
                      ...formData,
                      tipo: selectedOption ? selectedOption.value : "",
                    })
                  }
                  options={tipoOptions}
                  placeholder="Seleziona tipo..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label>Taglia:</label>
                <Select
                  value={
                    tagliaOptions.find(
                      (option) => option.value === formData.taglia
                    ) || null
                  }
                  onChange={(selectedOption) =>
                    setFormData({
                      ...formData,
                      taglia: selectedOption ? selectedOption.value : "",
                    })
                  }
                  options={tagliaOptions}
                  placeholder="Seleziona taglia..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label>Quantità:</label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantita}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quantita: parseInt(e.target.value) || 0,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Descrizione:</label>
                <textarea
                  value={formData.descrizione}
                  onChange={(e) =>
                    setFormData({ ...formData, descrizione: e.target.value })
                  }
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Data Acquisto:</label>
                <input
                  type="date"
                  value={formData.dataAcquisto}
                  onChange={(e) =>
                    setFormData({ ...formData, dataAcquisto: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowAddForm(false)}>
                  Annulla
                </button>
                <button type="submit" className="btn-primary">
                  Aggiungi
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Assegna Articolo */}
      {showAssignForm && selectedItem && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <div className="modal-header">
              <h3>Assegna {selectedItem.tipo}</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowAssignForm(false);
                  setSelectedItem(null);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAssign} className="assign-form">
              <div className="item-info">
                <p>
                  <strong>Articolo:</strong> {selectedItem.tipo} -{" "}
                  {selectedItem.taglia} - {selectedItem.colore}
                </p>
                <p>
                  <strong>Disponibili:</strong>{" "}
                  {selectedItem.quantitaDisponibile}
                </p>
              </div>

              <div className="form-group">
                <label>Dipendente:</label>
                <Select
                  value={
                    dipendenti && assignData.dipendenteId
                      ? {
                          value: assignData.dipendenteId,
                          label: assignData.dipendenteName,
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const selectedDip = dipendenti.find(
                        (d) => d.id === selectedOption.value
                      );
                      setAssignData({
                        ...assignData,
                        dipendenteId: selectedOption.value,
                        dipendenteName: selectedDip
                          ? `${
                              selectedDip.anagrafica?.nome &&
                              selectedDip.anagrafica?.cognome
                                ? `${selectedDip.anagrafica.nome} ${selectedDip.anagrafica.cognome}`
                                : selectedDip.displayName
                            }${selectedDip.role === "admin" ? " (Admin)" : ""}`
                          : "",
                      });
                    } else {
                      setAssignData({
                        ...assignData,
                        dipendenteId: "",
                        dipendenteName: "",
                      });
                    }
                  }}
                  options={
                    dipendenti
                      ? dipendenti.map((dipendente) => ({
                          value: dipendente.id,
                          label: `${
                            dipendente.anagrafica?.nome &&
                            dipendente.anagrafica?.cognome
                              ? `${dipendente.anagrafica.nome} ${dipendente.anagrafica.cognome}`
                              : dipendente.displayName
                          }${dipendente.role === "admin" ? " (Admin)" : ""}`,
                        }))
                      : []
                  }
                  placeholder="Seleziona dipendente..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                  }}
                  isClearable
                  required
                />
              </div>

              <div className="form-group">
                <label>Quantità da assegnare:</label>
                <input
                  type="number"
                  min="1"
                  max={selectedItem.quantitaDisponibile}
                  value={assignData.quantitaAssegnata}
                  onChange={(e) =>
                    setAssignData({
                      ...assignData,
                      quantitaAssegnata: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Data Assegnazione:</label>
                <input
                  type="date"
                  value={assignData.dataAssegnazione}
                  onChange={(e) =>
                    setAssignData({
                      ...assignData,
                      dataAssegnazione: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Note:</label>
                <textarea
                  value={assignData.note}
                  onChange={(e) =>
                    setAssignData({ ...assignData, note: e.target.value })
                  }
                  rows="3"
                  placeholder="Note aggiuntive (opzionale)"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignForm(false);
                    setSelectedItem(null);
                  }}
                >
                  Annulla
                </button>
                <button type="submit" className="btn-primary">
                  Assegna
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
